import axios, { type AxiosInstance } from 'axios'
import { CheckFeature } from './use-cases/CheckFeature'
import { FeatureFlipCacheDataSource } from './infrastructure/FeatureFlipCacheDataSource'
import { FeatureFlipEnvDataSource, type EnvSource } from './infrastructure/FeatureFlipEnvDataSource'
import { FeatureFlipIzanamiDataSource } from './infrastructure/FeatureFlipIzanamiDataSource'
import { FeatureFlipRepository } from './infrastructure/FeatureFlipRepository'
import type { Feature } from './domain/Feature'

const DEFAULT_BRAND = 'aladin'

export interface FeatureFlippingOptions {
  featureFlippingApiHost: string
  /** Prefixes Izanami feature ids (`<brand>:<feature-id>`). Defaults to 'aladin'. */
  brand?: string
  /** Where Env-type flags are read from. Defaults to `window.env`. */
  envSource?: EnvSource
  errorLogger?: (e: Error) => void
}

let providedOptions: FeatureFlippingOptions | undefined
let checkFeatureInstance: CheckFeature | undefined

const windowEnv: EnvSource = (id) => {
  const env = (window as unknown as { env?: Record<string, string | undefined> }).env
  return env?.[id]
}

/**
 * Configures the feature flipping singleton. Call it once at app bootstrap,
 * before the first feature check. Apps running in an iframe (micro-frontends)
 * must call it since their own `window.env` does not hold the host config.
 */
export function provideFeatureFlipping(options: FeatureFlippingOptions) {
  providedOptions = options
  checkFeatureInstance = undefined
}

/**
 * @deprecated For unit test only
 */
export function reset() {
  providedOptions = undefined
  checkFeatureInstance = undefined
}

/** Hosts exposing their runtime config on `window.env` work without an explicit provide. */
function resolveOptions(): FeatureFlippingOptions {
  if (providedOptions) return providedOptions
  return {
    featureFlippingApiHost: windowEnv('FEATURE_FLIPPING_API_HOST') ?? '',
    brand: windowEnv('VUE_APP_CONTEXT'),
  }
}

/** No-network fallback when the host is missing: dynamic flags stay inactive instead of firing doomed requests. */
function createUnconfiguredClient(errorLogger: (e: Error) => void): Pick<AxiosInstance, 'post'> {
  errorLogger(new Error('Feature flipping: FEATURE_FLIPPING_API_HOST is not configured, dynamic feature flips are inactive'))
  return {
    post: async () => ({ data: { active: false } }),
  } as unknown as Pick<AxiosInstance, 'post'>
}

function getCheckFeature(): CheckFeature {
  if (checkFeatureInstance) return checkFeatureInstance

  const options = resolveOptions()
  const errorLogger = options.errorLogger ?? console.error
  const izanamiClient = options.featureFlippingApiHost
    ? axios.create({
        baseURL: options.featureFlippingApiHost,
        headers: { Accept: 'application/json' },
      })
    : createUnconfiguredClient(errorLogger)

  checkFeatureInstance = new CheckFeature(
    new FeatureFlipRepository(
      new FeatureFlipIzanamiDataSource(izanamiClient, errorLogger),
      new FeatureFlipEnvDataSource(errorLogger, options.envSource ?? windowEnv),
      new FeatureFlipCacheDataSource(),
      options.brand || DEFAULT_BRAND,
    ),
  )
  return checkFeatureInstance
}

export const checkFeature = {
  execute(feature: Feature, options: { forceUpdate?: boolean } = {}): Promise<boolean> {
    return getCheckFeature().execute(feature, options)
  },
}
